import React from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { Card } from "react-bootstrap";
import logo from "../../../../src/assets/substratum-protocol/logo-substratum-protocol.webp";

import "../../../index.css";
import { ContentContainer } from "../SessionSection";
import { useEffect } from "react";
import "./substratum-protocol-styles.scss";

// These illustrations already exist in the Session 03 asset directory and are
// reused here to preserve a working build without assuming new asset files.
import sector09 from "../../../../src/assets/substratum-protocol/session04/sector-09-mood-inner-core.webp";
import sector10 from "../../../../src/assets/substratum-protocol/session04/sector-10-mood-the-anomaly.webp";
import sector08 from "../../../../src/assets/substratum-protocol/session04/sector-08-mood-barrier-event-horizon.webp";
import necklace from "../../../../src/assets/substratum-protocol/session04/burns-purple-necklace.webp";
import clue36 from "../../../../src/assets/substratum-protocol/session04/anomoly-clue-36.webp";
import aliens from "../../../../src/assets/substratum-protocol/session04/sector-08-encounter-first-civilization.webp";
import clue45 from "../../../../src/assets/substratum-protocol/session04/anomoly-clue-45.webp";
import finalBurns from "../../../../src/assets/substratum-protocol/session04/final-hypothesis-burns.webp";
import finalMoshe from "../../../../src/assets/substratum-protocol/session04/final-hypothesis-moshe.webp";

const pageHeader = {
  color: "white",
  backgroundColor: "black",
  opacity: "1",
  marginTop: "20px",
  padding: "15px",
};

const cardStyle = {
  textAlign: "left",
  fontFamily: "Carter One",
  backgroundColor: "rgba(0, 0, 0, 1)",
  color: "white",
  marginBottom: "15px",
};

const SPSession04 = () => {
  const section1Data = [
    {
      title: "",
      sectionTitleCssName: "sectionTitleSubstratumProtocol",
      text: (
        <div className="substratumProtocolBodyText">
          <p>
            <h3 style={{ color: "#c47768" }}>
              WARNING: session notes for Substratum Protocol contain spoilers.
              The discoveries, interpretations, and conclusions recorded here
              reflect our group's campaign and should be avoided by anyone who
              wants to experience the story without prior knowledge.
            </h3>
          </p>
          <p>
            The fourth session resumes in{" "}
            <strong>Sector 07: Alpha Particle Grove</strong>. Burns and Moshe
            have just survived a violent confrontation with a strange humanoid
            wielding an oval energy weapon. The creature lies dead beside the
            crashed spherical craft from which it emerged. Its weapon has gone
            dark, and the immediate danger has passed.
          </p>
          <p>
            There is little time to celebrate. The injured woman they were
            trying to help is now lying on the ground, her chest rising in
            desperate, shallow movements. Her purple necklace glows faintly.
            Burns turns away from the dead attacker and returns to the woman,
            while Moshe keeps watch over the strange craft.
          </p>
          <p>
            Burns watches helplessly as the woman struggles to breathe. Her hand
            reaches for his and weakly drags his hand to the necklace. Then, her
            eyes go flat and lifeless. She is gone.
          </p>
          <a href="#pageTop">Back to Top</a>
        </div>
      ),
    },
  ];

  const section2Data = [
    {
      title: "THE PURPLE NECKLACE",
      sectionTitleCssName: "sectionTitleSubstratumProtocol",
      text: (
        <div className="substratumProtocolBodyText">
          <p>
            Burns studies the woman's tear-shaped purple necklace. Its glow
            weakens as her heart finishes beating. The material resembles purple
            jade, but its behavior suggests something more purposeful: the
            object appears to respond to the energy of a beating heart, or
            perhaps to augment it.
          </p>
          <p>
            <img
              src={necklace}
              alt="The Purple Necklace"
              className="imageWrapper"
            />
          </p>
          <p>
            Burns tries the necklace on himself. The change in its glow appears
            to depend on both the wearer's heartbeat and the necklace's
            proximity to the heart.
          </p>
          <p>
            Moshe examines the two bodies. The deceased figures are
            distinguishable as female and male, but their origins remain
            uncertain. The genitals of the male have a corkscrew-like structure,
            which is the only unusual feature beyond his skin color and red
            eyes. The woman seems fully human as we understand human anatomy.
            Are they humans from a distant past, humans from a possible future,
            or something that has evolved along a different path? The spherical
            craft appears to have crashed, although its presence hundreds of
            kilometers beneath the Earth's surface is difficult to explain.
          </p>
          <p>
            Burns contacts the Fracture Observatory. The team has two deceased
            humanoids and an injured stranger, and neither scientist is willing
            to leave the bodies behind or bury them without understanding what
            they have found. Dr. Almeida advises them to lay the dead in repose
            inside the sphere and attempt to close the sphere door if it can be
            done safely. From the available evidence, it resembles a kind of
            helicopter or transport craft.
          </p>
          <p>
            The BeepBoBops transmit images and data back to the Observatory. The
            scientists cannot yet explain how the visitors arrived, why they
            were here, or whether the necklace is medical technology,
            life-support equipment, or something else entirely.
          </p>
          <a href="#pageTop">Back to Top</a>
        </div>
      ),
    },
  ];

  const section3Data = [
    {
      title: "CLUE 36: THE BOOK OF BEINGS",
      sectionTitleCssName: "sectionTitleSubstratumProtocol",
      text: (
        <div className="substratumProtocolBodyText">
          <p>
            Moshe dashes inside the sphere, discovers a clue in book form, and
            he finds a button to close the sphere behind him. The alien book
            cataloguing beings of various types and races from different worlds.
            Its contents suggest a record of forms of life that the scientists
            cannot place within any familiar taxonomy. The book is not simply an
            archive of anatomy. It may be a record of entities, worlds, or
            states of existence that do not fit the scientists' assumptions
            about what life is.
          </p>
          <p>
            <img
              src={clue36}
              alt="Clue 36: The Book of Beings"
              className="imageWrapper"
            />
          </p>
          <p>
            Moshe's continuing exposure to the anomaly produces a system
            upgrade. The disorientation that has followed him since passing
            <strong> Beyond the Horizon</strong> begins to settle. It feels as
            though a spinning object is gradually slowing, bringing the vertigo
            of looking beyond ordinary reality into a more stable alignment.
          </p>
          <p>
            Burns takes a deep breath and prepares to travel onward. The
            scientists have learned that their equipment, their memories, and
            even their sense of direction may be inadequate to describe what
            they are encountering. The deeper they go, the less reliable their
            assumptions become.
          </p>
          <a href="#pageTop">Back to Top</a>
        </div>
      ),
    },
  ];

  const section4Data = [
    {
      title: "SECTOR 08: BARRIER EVENT HORIZON",
      sectionTitleCssName: "sectionTitleSubstratumProtocol",
      text: (
        <div className="substratumProtocolBodyText">
          <p>
            The Barrier Event Horizen sector is a place where the laws of
            physics seem to bend and twist, challenging the scientists'
            understanding of reality. All movement seems hyper-slow although
            each of the scientists feels they are moving normally. There are the
            skeletal remains of alien buildings and mineral structur
            intertwined, floating, spinning, and "falling" toward a central
            energy ball on the "horizon" of a vast, seemingly endless cavern.
          </p>
          <p>
            <img
              src={sector08}
              alt="Sector 08: Barrier Event Horizon"
              className="imageWrapper"
            />
          </p>
          <p>
            Their route leads to a colossal domed building; it resembles a giant
            bead of water having struck the ground and frozen. Outside the dome,
            are broken fountains and a broad sidewalk of black, gray, and purple
            stonework. The structure looks abandoned, yet it has not simply
            collapsed into ruin.
          </p>
          <p>
            Inside, the scientists find an alien foyer, rooms that resemble
            bathrooms, and fixtures designed for bodies of unfamiliar
            proportions. The seats and toilets are strangely narrow and
            positioned at an unexpected height. Their notebooks resemble
            illuminated floppy monitors. Along the edges of a moving belt, metal
            canes serve as controls: touch a cane, and the belt changes
            direction.
          </p>
          <p>
            The building is a civilization's home, not merely a ruin. On the
            second floor, Burns and Moshe use a BeepBoBop to explore a study.
            They notice that the drones movements seem to slow when the drone
            moves farther away. Time, or at least the experience of it, appears
            to be behaving differently here. At the top of a second escalator is
            a domed chamber. The BeepBoBop transmits images of tall, thin beings
            with wide, all-black almond eyes; humanoid but alien. One is
            operating controls on a column and the city beyond the windows
            appears whole, as if the ruins were restored or the windows show a
            different time or place than what one sees on the outside of this
            tear-shaped building.
          </p>
          <p>
            <img
              src={aliens}
              alt="Sector 08: Encounter with the First Civilization"
              className="imageWrapper"
            />
          </p>
          <p>
            Mosh and Burns stand cautiously as the alien inhabitants approach
            without hostility. Moshe lays down a sensor, while the scientists
            attempt to communicate through the BeepBoBops and their exosuit
            systems. The beings kneel toward the drone with the patient
            attention one might show a child.
          </p>
          <p>“We are those who remain.”</p>
          <p>“We live where the seconds become heavy.”</p>
          <p>
            Burns explains that they come from this planet as explorers and that
            their world is being destroyed. The inhabitants respond with
            questions that seem to misunderstand the premise.
          </p>
          <p>“You have not been here before.”</p>
          <p>“You are asking about a place you have not yet learned to see.”</p>
          <p>
            They tell the scientists that there is a great deal beyond, but also
            a great deal behind them. Both directions matter. When Burns points
            down and asks whether that is down, one of the beings replies: “You
            call it down, we call it forward.”
          </p>
          <p>
            The inhabitants describe something very old, something very new, and
            something that is happening. They insist that the planet is not
            being destroyed, though they offer no explanation that the
            scientists can readily understand.
          </p>
          <p>“You will understand when you stop asking what it is.”</p>
          <p>
            Moshe hands over the alien book. The being closest to them turns
            through it and seems to engage with the galaxy-like pattern in the
            ceiling. “Your memories. Your questions. Your fear. You think they
            are inside your heads. They are not.”
          </p>
          <p>
            The statement hangs in the air, unsettling and unfinished. The
            inhabitants immediately change the subject, offering no
            clarification. Their final warning is quiet rather than threatening:
          </p>
          <p>
            “Go carefully. The place below is not cruel. It simply does not know
            that you are fragile.”
          </p>
          <p>
            Then, the room fades and the scientists are standing in the next
            sector.
          </p>
          <a href="#pageTop">Back to Top</a>
        </div>
      ),
    },
  ];

  const section5Data = [
    {
      title: "SECTOR 09: THE INNER CORE",
      sectionTitleCssName: "sectionTitleSubstratumProtocol",
      text: (
        <div className="substratumProtocolBodyText">
          <p>
            Beyond the civilization lies a chamber dominated by a colossal
            computer terminal. It stands in an otherwise empty space, its
            systems holding records of the anomaly, its appearance, and events
            that occurred long before humanity discovered the Fracture.
          </p>
          <p>
            <img
              src={sector09}
              alt="Mood of the inner core in Sector 09"
              className="imageWrapper"
            />
          </p>
          <p>
            The archive may be the best chance the scientists have to understand
            what they have seen. Burns prepares BeepBoBop to crawl across the
            terminal and investigate its surface. The system responds with a
            defensive energy grid. A burst of energy lashes across the chamber,
            turning the attempt to study the archive into a dangerous encounter.
          </p>
          <p>
            Burns tries to evade the attack but is slashed by laser-like bursts
            of pure energy. Moshe chooses to endure the strike and continue
            studying the system, taking additional Stress in exchange for
            information. The archive's defense is not random: it appears to be
            protecting something within the terminal.
          </p>

          <p>
            Moshe uncovers <strong>Clue 45</strong>: a pictographic piece of
            artwork; almost a mixture of cave painting and alien script. The
            pictographic appears on all the monitors of the behemoth computer.
            The discovery triggers a flashback to the conversation with the
            inhabitants of Sector 08. Their cryptic comments become newly
            relevant: the pattern of what the scientists perceive may matter
            more than the objects they believe they are observing.
          </p>
          <p>
            <img src={clue45} alt="The Pictograph" className="imageWrapper" />
          </p>
          <p>
            Burns takes a deep breath and uses the memory of an earlier
            conversation to reinterpret the archive's defenses. He recalls
            learning to code and recognizing a pattern beneath a system that
            initially appeared incomprehensible. The key may not be to fight the
            defensive grid, but to understand the rules by which it perceives
            and responds.
          </p>
          <p>
            The defensive system activates again. Burns manages to avoid its
            attack, while Moshe is pushed to the edge of his Stress capacity.
            The scientists have gained another fragment of understanding, but
            the cost of approaching the archive is becoming severe.
          </p>
          <a href="#pageTop">Back to Top</a>
        </div>
      ),
    },
  ];

  const section6Data = [
    {
      title: "SECTION 10: THE ANOMALY",
      sectionTitleCssName: "sectionTitleSubstratumProtocol",
      text: (
        <div className="substratumProtocolBodyText">
          <p>
            At last, Burns and Moshe reach the anomaly itself. The accumulated
            evidence from the Fracture now begins to form a single, staggering
            hypothesis. The star in the jar, the miniature black hole, the
            gravitational altar, the alien archive, and the inhabitants who
            speak of memories outside the mind may all be expressions of one
            underlying system.
          </p>
          <p>
            <img
              src={sector10}
              alt="Mood of the inner core in Sector 10"
              className="imageWrapper"
            />
          </p>
          <p>
            The final hypothesis is not that the Earth is a conventional planet
            being damaged from the outside. It is that the Earth may have been
            misunderstood from the beginning. What appears to be a world
            containing life may instead be a vast living container, a form of
            internal consciousness within which worlds, beings, and experiences
            arise.
          </p>
          <p>
            The anomaly may be an advanced technology seeded through space. Over
            millions of years it replicates, creating planets that act as seeds.
            As each world develops, it eventually provides access to
            technologies that can manipulate gravity, contain stars, and travel
            through space. The planet is not merely a home; it is a seed
            flowering into a new level of possibility.
          </p>
          <p>
            The theory reframes even the horrors of the Fracture. The screams,
            pain, death, and destruction may be part of a cycle that captures
            the data of worlds and their inhabitants. The alien book records
            destroyed worlds because the process is repeated. Consciousness
            moves through containers, transforms, and begins again. Nothing may
            be dying in the ultimate sense; the cycle continues.
          </p>
          <p>
            The anomaly maintains itself. Its purpose is not conquest or
            destruction, but choice: the possibility for individual
            consciousness to remain within its container or move beyond it. Each
            person may have a different journey, and the choice itself may
            repeat infinitely.
          </p>
          <p>
            Under this hypothesis, the Earth's molten iron core is not what
            conventional science has described. That model was a way of
            perceiving the world from within a contained existence. There may be
            no molten core at all, only energy. The anomaly cannot become
            unstoppable, because replication is its nature. The ultimate end
            would come only when there are no souls left to transform.
          </p>
          <p>
            The scientists are left with an impossible question. If the anomaly
            is offering a choice, what does it mean to repair it? Is the goal to
            preserve the familiar world, or to allow the process to complete
            itself? Their answers diverge, and the future of the Earth depends
            on what they choose to believe.
          </p>
          <a href="#pageTop">Back to Top</a>
        </div>
      ),
    },
  ];

  const section7Data = [
    {
      title: "BURNS: A CHOICE TO TRANSCEND",
      sectionTitleCssName: "sectionTitleSubstratumProtocol",
      text: (
        <div className="substratumProtocolBodyText">
          <p>
            <img
              src={finalBurns}
              alt="Final Hypothesis: Burns"
              style={{
                maxWidth: "30%",
                padding: "10px",
                height: "auto",
                float: "left",
              }}
              className="imageWrapper"
            />
          </p>
          <p>
            For Burns, the solution returns to the question of choice. He has
            passed through an experience resembling the bardo: a threshold where
            consciousness may move to a higher state or return to the reality it
            has known. He sees the anomaly not simply as a threat to be stopped,
            but as a decision every person may be facing in this very moment.
          </p>
          <p>
            Burns imagines several possible outcomes. He could leave ordinary
            reality and live in a new one. He could return to his former life,
            perhaps with another chance to understand what has happened. He
            could die, with no further journey and no choice beyond the life he
            has experienced. Or he could wait, unable to decide until he hears
            Moshe.
          </p>
          <p>
            Ultimately, Burns chooses transcendence. In his vision, all
            consciousness moves onward. Everyone walks the Earth as a Buddha,
            and humanity continues, enlightened, rebuilding civilization through
            a new understanding of existence. The new world is not necessarily a
            distant celestial realm. It is still Earth, but Earth understood and
            experienced in a radically different way.
          </p>
          <p>
            The old world may have been an illusion, and what follows is a new
            Earth. Burns sees the possibility of transformation rather than
            simple annihilation: the familiar world ends, but consciousness
            continues into a new form.
          </p>
          <a href="#pageTop">Back to Top</a>
        </div>
      ),
    },
  ];

  const section8Data = [
    {
      title: "MOSHE: ACCELERATE THE ANOMALY",
      sectionTitleCssName: "sectionTitleSubstratumProtocol",
      text: (
        <div className="substratumProtocolBodyText">
          <p>
            <img
              src={finalMoshe}
              alt="Final Hypothesis: Moshe"
              style={{
                maxWidth: "30%",
                padding: "10px",
                height: "auto",
                float: "left",
              }}
              className="imageWrapper"
            />
          </p>
          <p>
            Moshe's experience leads him toward a very different conclusion. He
            is disoriented, speaking to himself, and has spent so long beyond
            the horizon that the anomaly seems capable of influencing him from
            within. He believes he has a psychic link to it. Whether this is
            genuine insight, a consequence of his exposure, or both remains
            uncertain.
          </p>
          <p>
            Moshe believes he has been chosen to accelerate the anomaly and
            bring the current age to an end. In his understanding, the anomaly
            chose him, and he is meant to help it complete its work. The Earth,
            which he thinks of as his brother, may appear to be destroyed, but
            the destruction may be the passage into another age rather than the
            end of everything.
          </p>
          <p>
            In Moshe's vision, the planet is vaporized and the old world dies.
            Yet a seed remains. Over another millennium or two, plants grow
            again and a new world returns. The people who lived through the
            transition may be gone, but something of them persists, carried
            forward into the next cycle.
          </p>
          <p>
            The two visions need not cancel one another out. Burns sees
            consciousness moving into enlightenment; Moshe sees the old age
            destroyed so a new one can bloom. They may be witnessing different
            moments of the same process, or different possibilities shaped by
            the choices they make.
          </p>
          <p>
            The final question remains unresolved: when the scientists trigger
            the anomaly, does everyone have a choice, or has the choice already
            been made for them?
          </p>
          <p>
            The End. But, also a new beginning in some time, somewhere beyond
            the horizon.
          </p>
          <a href="#pageTop">Back to Top</a>
        </div>
      ),
    },
  ];

  useEffect(() => {
    document.body.style.backgroundColor = "#c47768";
    document.body.style.backgroundWidth = "100vw";
    document.body.backgroundHeight = "100vh";
    document.body.style.backgroundSize = "cover";
  }, []);

  return (
    <Container>
      <Row>
        <Col>
          <div
            style={{
              backgroundColor: "transparent",
              fontFamily: "IM Fell Great Primer",
            }}
          >
            <div style={pageHeader}>
              <main role="main" className="inner cover text-center">
                <a name="pageTop" href="#pageTop">
                  <img
                    src={logo}
                    style={{
                      maxWidth: "400px",
                      backgroundColor: "white",
                      borderRadius: "10px",
                    }}
                    alt="Substratum Protocol campaign"
                  />
                </a>
              </main>
              <h1 className="cover-heading">THE LAST HYPOTHESIS</h1>
              <h4>Substratum Protocol Session 04</h4>
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card style={cardStyle}>
            <Card.Body>
              <Card.Title
                id="cardTitle-substratum-protocol"
                className="cardTitle-substratum-protocol"
              >
                <span
                  style={{
                    color: "black",
                    textAlign: "center",
                  }}
                >
                  Burns and Moshe investigate the crashed sphere, encounter a
                  civilization beyond ordinary time, uncover the anomaly's
                  archive, and face a final choice about consciousness, the
                  Earth, and the future of existence.
                </span>
                <br />
                <small>
                  <span className="emoji" role="img" aria-label="lab coat">
                    🥼
                  </span>
                  <a href="./substratum-protocol-scientist-profile-moshe-mazel">
                    Dr. Moshe Mazel
                  </a>
                  <span className="emoji" role="img" aria-label="lab coat">
                    🥼
                  </span>
                  <a href="./substratum-protocol-scientist-profile-burnstein-grok">
                    Dr. Burnstein Grok
                  </a>
                  <br />
                  <span className="emoji" role="img" aria-label="earth">
                    🌎
                  </span>
                  <a href="./substratum-protocol-session-03">
                    Session 03: The Gravity of Darkness
                  </a>
                  <span className="emoji" role="img" aria-label="cosmos">
                    ✨
                  </span>
                  Session 04: The Last Hypothesis
                </small>
              </Card.Title>
              <div>
                <ContentContainer data={section1Data} />
              </div>
              <div>
                <ContentContainer data={section2Data} />
              </div>
              <div>
                <ContentContainer data={section3Data} />
              </div>
              <div>
                <ContentContainer data={section4Data} />
              </div>
              <div>
                <ContentContainer data={section5Data} />
              </div>
              <div>
                <ContentContainer data={section6Data} />
              </div>
              <div>
                <ContentContainer data={section7Data} />
              </div>
              <div>
                <ContentContainer data={section8Data} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default SPSession04;
